import React, {
  useEffect,
  useMemo,
} from "react";

import {
  MANUSCRIPT_IMPORT_DECISIONS,
  allowedManuscriptImportDecisions,
  buildManuscriptImportDecisionRows,
  nextManuscriptImportDecision,
} from "./ChildrensBookManuscriptImportDecision";

import {
  countWordsFromManuscriptEntry,
  plainTextFromManuscriptEntry,
} from "../utils/childrensBookManuscriptContent";


function clean(value) {
  return value == null
    ? ""
    : String(value).trim();
}


function shortText(
  value,
  limit = 240
) {
  const text =
    clean(value).replace(
      /\s+/g,
      " "
    );

  if (
    !text ||
    text.length <= limit
  ) {
    return text;
  }

  return `${
    text.slice(
      0,
      Math.max(
        1,
        limit - 1
      )
    ).trim()
  }…`;
}


function writerExpectedResult(
  row,
  decision,
  isFR
) {
  const importedWords =
    Number(
      row?.importedWordCount ||
      0
    );

  const existingWords =
    Number(
      row?.existingWordCount ||
      0
    );

  const place =
    clean(
      row?.displayTargetLabel ||
      row?.targetLabel
    ) ||
    (isFR
      ? "cet endroit du livre"
      : "this place in the book");

  if (
    decision ===
    MANUSCRIPT_IMPORT_DECISIONS
      .SKIP
  ) {
    return {
      tone:
        "neutral",

      title: isFR
        ? "Cette section restera en dehors du livre"
        : "This section will be left out of the book",

      body: isFR
        ? "Le contenu actuel du livre restera exactement comme il est."
        : "The current book content will remain exactly as it is.",
    };
  }

  if (!row?.placed) {
    return {
      tone:
        "attention",

      title: isFR
        ? "Votre choix est encore nécessaire"
        : "Your choice is still needed",

      body: isFR
        ? "Choisissez la page ou la double page où cette section doit apparaître."
        : "Choose the page or two-page spread where this section should appear.",
    };
  }

  if (
    decision ===
    MANUSCRIPT_IMPORT_DECISIONS
      .APPEND
  ) {
    return {
      tone:
        "safe",

      title: isFR
        ? "Le texte actuel restera, puis cette section sera ajoutée"
        : "The current text will stay, followed by this section",

      body: isFR
        ? `${place} conservera ses ${existingWords.toLocaleString()} mots actuels. Les ${importedWords.toLocaleString()} mots importés seront ajoutés après. Total prévu : ${(existingWords + importedWords).toLocaleString()} mots.`
        : `${place} will keep its ${existingWords.toLocaleString()} current words. The ${importedWords.toLocaleString()} imported words will be added afterward. Expected total: ${(existingWords + importedWords).toLocaleString()} words.`,
    };
  }

  if (
    decision ===
    MANUSCRIPT_IMPORT_DECISIONS
      .ADD
  ) {
    return {
      tone:
        "safe",

      title: isFR
        ? "Cette section deviendra le texte de cet endroit"
        : "This section will become the text for this place",

      body: isFR
        ? `${place} est actuellement vide. Il contiendra les ${importedWords.toLocaleString()} mots importés.`
        : `${place} is currently empty. It will contain the ${importedWords.toLocaleString()} imported words.`,
    };
  }

  if (
    decision ===
    MANUSCRIPT_IMPORT_DECISIONS
      .REPLACE
  ) {
    return {
      tone:
        "danger",

      title: isFR
        ? "Le texte actuellement dans le livre sera retiré"
        : "The text currently in the book will be removed",

      body: isFR
        ? `${place} perdra ses ${existingWords.toLocaleString()} mots actuels. Il contiendra ensuite les ${importedWords.toLocaleString()} mots de cette section Word.`
        : `${place} will lose its ${existingWords.toLocaleString()} current words. It will then contain the ${importedWords.toLocaleString()} words from this Word section.`,
    };
  }

  return {
    tone:
      "attention",

    title: isFR
      ? "Choisissez ce qui doit arriver"
      : "Choose what should happen",

    body: isFR
      ? "Rien ne changera tant que votre choix ne sera pas clair."
      : "Nothing will change until your choice is clear.",
  };
}


function expectedResultStyle(
  tone
) {
  if (
    tone ===
    "danger"
  ) {
    return {
      border:
        "2px solid #ef4444",
      background:
        "#fff1f2",
      color:
        "#991b1b",
    };
  }

  if (
    tone ===
    "safe"
  ) {
    return {
      border:
        "2px solid #4ade80",
      background:
        "#f0fdf4",
      color:
        "#166534",
    };
  }

  if (
    tone ===
    "attention"
  ) {
    return {
      border:
        "2px solid #fb923c",
      background:
        "#fff7ed",
      color:
        "#9a3412",
    };
  }

  return {
    border:
      "2px solid #cbd5e1",
    background:
      "#f8fafc",
    color:
      "#334155",
  };
}


function sameDecisions(
  first = {},
  second = {}
) {
  const left =
    Object.entries(first)
      .filter(([, value]) => clean(value))
      .sort();

  const right =
    Object.entries(second)
      .filter(([, value]) => clean(value))
      .sort();

  return (
    JSON.stringify(left) ===
    JSON.stringify(right)
  );
}


function pageStartNumber(
  unit,
  index,
  units = []
) {
  const explicit =
    Number.parseInt(
      clean(
        unit?.pageNumber
      ),
      10
    );

  if (
    Number.isFinite(explicit) &&
    explicit > 0
  ) {
    return explicit;
  }

  return (
    (
      Array.isArray(units)
        ? units
        : []
    )
      .slice(
        0,
        Math.max(
          0,
          index
        )
      )
      .reduce(
        (
          page,
          previousUnit
        ) =>
          page +
          (
            previousUnit
              ?.unitType ===
            "double_page_spread"
              ? 2
              : 1
          ),
        1
      )
  );
}


function destinationLabel(
  unit,
  index,
  isFR,
  units = []
) {
  const firstPage =
    pageStartNumber(
      unit,
      index,
      units
    );

  const isSpread =
    unit?.unitType ===
    "double_page_spread";

  if (isSpread) {
    const lastPage =
      firstPage + 1;

    return isFR
      ? `Double page — Pages ${firstPage} à ${lastPage}`
      : `Two-page spread — Pages ${firstPage} to ${lastPage}`;
  }

  return isFR
    ? `Page ${firstPage}`
    : `Page ${firstPage}`;
}


export function recommendedImportDecisions(
  rows = [],
  current = {}
) {
  const next = {};

  rows.forEach((row) => {
    const existing =
      clean(
        current?.[
          row.sectionId
        ]
      );

    const allowed =
      allowedManuscriptImportDecisions(
        row
      );

    if (
      existing &&
      allowed.includes(existing)
    ) {
      next[row.sectionId] =
        existing;

      return;
    }

    if (!row.placed) {
      return;
    }

    next[row.sectionId] =
      Number(
        row.existingWordCount ||
        0
      ) > 0
        ? MANUSCRIPT_IMPORT_DECISIONS
            .APPEND
        : MANUSCRIPT_IMPORT_DECISIONS
            .ADD;
  });

  return next;
}


export function unifiedImportSummary(
  rows = [],
  decisions = {}
) {
  let ready = 0;
  let attention = 0;
  let skipped = 0;
  let replacements = 0;

  rows.forEach((row) => {
    const decision =
      clean(
        decisions?.[
          row.sectionId
        ]
      );

    const allowed =
      allowedManuscriptImportDecisions(
        row
      );

    if (
      !decision ||
      !allowed.includes(decision)
    ) {
      attention += 1;
      return;
    }

    if (
      decision ===
      MANUSCRIPT_IMPORT_DECISIONS
        .SKIP
    ) {
      skipped += 1;
      return;
    }

    ready += 1;

    if (
      decision ===
      MANUSCRIPT_IMPORT_DECISIONS
        .REPLACE
    ) {
      replacements += 1;
    }
  });

  return {
    ready,
    attention,
    skipped,
    replacements,
  };
}


export default function ChildrensBookManuscriptImportUnifiedReview({
  lang = "en",
  sections = [],
  bookMapUnits = [],
  manuscriptEntries = [],
  mappingBySection = {},
  decisionBySection = {},
  onMappingChange,
  onDecisionChange,
}) {
  const isFR =
    String(lang).toLowerCase() ===
    "fr";

  const units = useMemo(
    () =>
      Array.isArray(bookMapUnits)
        ? bookMapUnits
        : [],
    [
      bookMapUnits,
    ]
  );

  const rows = useMemo(
    () =>
      buildManuscriptImportDecisionRows(
        sections,
        mappingBySection,
        units,
        manuscriptEntries,
        lang
      ).map(
        (row) => {
          const entry =
            (
              Array.isArray(
                manuscriptEntries
              )
                ? manuscriptEntries
                : []
            ).find(
              (candidate) =>
                clean(
                  candidate?.unitId
                ) ===
                clean(
                  row?.unitId
                )
            );

          return {
            ...row,

            existingWordCount:
              countWordsFromManuscriptEntry(
                entry
              ),
          };
        }
      ),
    [
      sections,
      mappingBySection,
      units,
      manuscriptEntries,
      lang,
    ]
  );

  useEffect(() => {
    if (
      typeof onDecisionChange !==
      "function"
    ) {
      return;
    }

    const mappedUnitIds =
      Object.values(
        mappingBySection &&
        typeof mappingBySection ===
          "object" &&
        !Array.isArray(
          mappingBySection
        )
          ? mappingBySection
          : {}
      )
        .map(clean)
        .filter(Boolean);

    const everyMappedUnitIsReady =
      mappedUnitIds.every(
        (unitId) =>
          units.some(
            (unit) =>
              clean(
                unit?.id
              ) === unitId
          )
      );

    if (
      mappedUnitIds.length > 0 &&
      !everyMappedUnitIsReady
    ) {
      return;
    }

    const recommended =
      recommendedImportDecisions(
        rows,
        decisionBySection
      );

    if (
      !sameDecisions(
        recommended,
        decisionBySection
      )
    ) {
      onDecisionChange(
        recommended
      );
    }
  }, [
    rows,
    mappingBySection,
    units,
    decisionBySection,
    onDecisionChange,
  ]);

  const summary =
    unifiedImportSummary(
      rows,
      decisionBySection
    );

  function changeDestination(
    sectionId,
    unitId
  ) {
    const selected =
      clean(unitId);

    const nextMapping = {
      ...mappingBySection,
    };

    Object.keys(nextMapping)
      .forEach((key) => {
        if (
          key !== sectionId &&
          selected &&
          clean(nextMapping[key]) ===
            selected
        ) {
          delete nextMapping[key];
        }
      });

    if (selected) {
      nextMapping[sectionId] =
        selected;
    } else {
      delete nextMapping[
        sectionId
      ];
    }

    if (
      typeof onDecisionChange ===
      "function"
    ) {
      const nextDecisions = {
        ...decisionBySection,
      };

      const existingDecision =
        clean(
          nextDecisions[
            sectionId
          ]
        );

      if (
        existingDecision !==
        MANUSCRIPT_IMPORT_DECISIONS
          .SKIP
      ) {
        delete nextDecisions[
          sectionId
        ];
      }

      onDecisionChange(
        nextDecisions
      );
    }

    if (
      typeof onMappingChange ===
      "function"
    ) {
      onMappingChange(
        nextMapping
      );
    }
  }

  function changeDecision(
    sectionId,
    decision
  ) {
    if (
      typeof onDecisionChange !==
      "function"
    ) {
      return;
    }

    onDecisionChange(
      nextManuscriptImportDecision(
        decisionBySection,
        sectionId,
        decision
      )
    );
  }

  const actionLabels = {
    skip: isFR
      ? "Laisser cette section en dehors du livre"
      : "Leave this section out of the book",

    add: isFR
      ? "Placer cette section ici"
      : "Place this section here",

    append: isFR
      ? "Conserver le texte actuel, puis ajouter cette section"
      : "Keep the current text, then add this section",

    replace: isFR
      ? "Retirer le texte actuel et utiliser cette section à la place"
      : "Remove the current text and use this section instead",
  };

  return (
    <section
      aria-labelledby="unified-import-review-title"
      style={{
        marginTop: 18,
        padding: 18,
        border:
          "1px solid #c4b5fd",
        borderRadius: 16,
        background: "#faf7ff",
      }}
    >
      <h4
        id="unified-import-review-title"
        style={{
          margin: 0,
          fontSize: 22,
        }}
      >
        {isFR
          ? "Voyez comment le manuscrit Word s’intégrera dans votre livre"
          : "How This Word Manuscript Will Fit into Your Book"}
      </h4>

      <p
        className="ns-card-meta"
        style={{
          margin: "7px 0 0",
        }}
      >
        {isFR
          ? "NextScenes a trouvé les sections ci-dessous. Pour chacune, choisissez où elle doit apparaître et ce qui doit arriver au texte déjà présent. Rien ne changera avant votre approbation finale."
          : "NextScenes found the sections below. For each one, choose where it should appear and what should happen to text already in the book. Nothing will change before your final approval."}
      </p>

      <div
        role="status"
        aria-label={
          isFR
            ? "Résumé de l’import"
            : "Import summary"
        }
        style={{
          marginTop: 14,
          padding: 12,
          borderRadius: 11,
          border:
            summary.attention > 0
              ? "1px solid #fdba74"
              : "1px solid #86efac",
          background:
            summary.attention > 0
              ? "#fff7ed"
              : "#f0fdf4",
          fontWeight: 650,
        }}
      >
        {isFR
          ? summary.ready === 1
            ? "1 section est prête."
            : `${summary.ready} sections sont prêtes.`
          : summary.ready === 1
            ? "1 section is ready."
            : `${summary.ready} sections are ready.`}
        {" "}
        {isFR
          ? summary.attention === 1
            ? "1 section demande votre attention."
            : `${summary.attention} sections demandent votre attention.`
          : summary.attention === 1
            ? "1 section needs your attention."
            : `${summary.attention} sections need your attention.`}
        {" "}
        {isFR
          ? "Rien n’a encore changé."
          : "Nothing has changed yet."}
      </div>

      <div
        style={{
          display: "grid",
          gap: 14,
          marginTop: 15,
        }}
      >
        {rows.map((row, index) => {
          const title =
            clean(row.title) ||
            `Section ${index + 1}`;

          const selectedDecision =
            clean(
              decisionBySection?.[
                row.sectionId
              ]
            );

          const allowed =
            allowedManuscriptImportDecisions(
              row
            );

          const selectedUnitIndex =
            units.findIndex(
              (unit) =>
                clean(
                  unit?.id
                ) ===
                clean(
                  row.unitId
                )
            );

          const selectedUnit =
            selectedUnitIndex >= 0
              ? units[
                  selectedUnitIndex
                ]
              : null;

          const displayTargetLabel =
            selectedUnit
              ? destinationLabel(
                  selectedUnit,
                  selectedUnitIndex,
                  isFR,
                  units
                )
              : "";

          const importedSection =
            (
              Array.isArray(
                sections
              )
                ? sections
                : []
            ).find(
              (section) =>
                clean(
                  section?.id
                ) ===
                row.sectionId
            );

          const importedText =
            clean(
              importedSection &&
              Object.prototype
                .hasOwnProperty.call(
                  importedSection,
                  "text"
                )
                ? importedSection.text
                : importedSection
                    ?.excerpt
            );

          const currentEntry =
            (
              Array.isArray(
                manuscriptEntries
              )
                ? manuscriptEntries
                : []
            ).find(
              (entry) =>
                clean(
                  entry?.unitId
                ) ===
                clean(
                  row.unitId
                )
            );

          const currentText =
            plainTextFromManuscriptEntry(
              currentEntry
            );

          const expectedResult =
            writerExpectedResult(
              {
                ...row,

                displayTargetLabel,
              },
              selectedDecision,
              isFR
            );

          const usedByOthers =
            new Set(
              Object.entries(
                mappingBySection
              )
                .filter(
                  ([sectionId]) =>
                    sectionId !==
                    row.sectionId
                )
                .map(([, unitId]) =>
                  clean(unitId)
                )
                .filter(Boolean)
            );

          return (
            <article
              key={row.sectionId}
              style={{
                padding: 15,
                borderRadius: 13,
                border:
                  row.placed
                    ? "1px solid #ddd6fe"
                    : "1px solid #fdba74",
                background: "#ffffff",
              }}
            >
              <h5
                style={{
                  margin: 0,
                  fontSize: 18,
                }}
              >
                {title}
              </h5>

              <p
                className="ns-card-meta"
                style={{
                  margin:
                    "5px 0 0",
                  fontSize: 13,
                  lineHeight: 1.45,
                  color:
                    "#64748b",
                }}
              >
                {isFR
                  ? `${Number(row.importedWordCount || 0).toLocaleString()} mots importés`
                  : `${Number(row.importedWordCount || 0).toLocaleString()} imported words`}
                {" · "}
                {row.placed
                  ? (
                      isFR
                        ? `${Number(row.existingWordCount || 0).toLocaleString()} mots existants`
                        : `${Number(row.existingWordCount || 0).toLocaleString()} existing words`
                    )
                  : (
                      isFR
                        ? "Destination non choisie"
                        : "Destination not chosen"
                    )}
              </p>

              <div
                style={{
                  display:
                    "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(235px, 1fr))",
                  gap: 13,
                  marginTop: 15,
                }}
              >
                <section
                  aria-label={
                    isFR
                      ? `Texte du document Word : ${title}`
                      : `Word document content for ${title}`
                  }
                  style={{
                    padding: 14,
                    borderRadius: 13,
                    border:
                      "2px solid #60a5fa",
                    background:
                      "#eff6ff",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 850,
                      color:
                        "#1d4ed8",
                      textTransform:
                        "uppercase",
                      letterSpacing:
                        "0.06em",
                    }}
                  >
                    {isFR
                      ? "Du document Word"
                      : "From the Word document"}
                  </div>

                  <p
                    style={{
                      margin:
                        "9px 0 0",
                      lineHeight: 1.6,
                      color:
                        "#1e293b",
                    }}
                  >
                    {shortText(
                      importedText
                    ) ||
                      (isFR
                        ? "Aucun extrait n’est disponible."
                        : "No text excerpt is available.")}
                  </p>
                </section>

                <section
                  aria-label={
                    isFR
                      ? `Contenu actuel du livre : ${title}`
                      : `Current book content for ${title}`
                  }
                  style={{
                    padding: 14,
                    borderRadius: 13,
                    border:
                      row.placed
                        ? "2px solid #f59e0b"
                        : "2px solid #cbd5e1",
                    background:
                      row.placed
                        ? "#fffbeb"
                        : "#f8fafc",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 850,
                      color:
                        row.placed
                          ? "#92400e"
                          : "#475569",
                      textTransform:
                        "uppercase",
                      letterSpacing:
                        "0.06em",
                    }}
                  >
                    {isFR
                      ? "Dans votre livre actuellement"
                      : "In your book now"}
                  </div>

                  <div
                    style={{
                      marginTop: 8,
                      fontWeight: 850,
                    }}
                  >
                    {row.placed
                      ? displayTargetLabel
                      : isFR
                        ? "Cette section n’a pas encore été placée dans le livre"
                        : "This section has not been placed in the book yet."}
                  </div>

                  <p
                    style={{
                      margin:
                        "7px 0 0",
                      lineHeight: 1.55,
                      color:
                        "#334155",
                    }}
                  >
                    {row.placed
                      ? Number(
                          row.existingWordCount ||
                          0
                        ) > 0
                        ? isFR
                          ? `${Number(row.existingWordCount || 0).toLocaleString()} mots sont déjà écrits ici.`
                          : `${Number(row.existingWordCount || 0).toLocaleString()} words are already written here.`
                        : isFR
                          ? "Cet endroit du livre est actuellement vide."
                          : "This place in the book is currently empty."
                      : isFR
                        ? "Choisissez d’abord où cette section doit apparaître."
                        : "First choose where this section should appear."}
                  </p>

                  {currentText ? (
                    <details
                      style={{
                        marginTop: 10,
                      }}
                    >
                      <summary
                        style={{
                          cursor:
                            "pointer",
                          fontWeight:
                            750,
                        }}
                      >
                        {isFR
                          ? "Lire le texte du livre qui serait touché"
                          : "Read the book text that would be affected"}
                      </summary>

                      <p
                        style={{
                          margin:
                            "8px 0 0",
                          whiteSpace:
                            "pre-wrap",
                          lineHeight:
                            1.6,
                        }}
                      >
                        {shortText(
                          currentText,
                          360
                        )}
                      </p>
                    </details>
                  ) : null}
                </section>

                <section
                  aria-live="polite"
                  aria-label={
                    isFR
                      ? `Résultat prévu pour ${title}`
                      : `Expected result for ${title}`
                  }
                  style={{
                    padding: 14,
                    borderRadius: 13,
                    ...expectedResultStyle(
                      expectedResult
                        .tone
                    ),
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 850,
                      textTransform:
                        "uppercase",
                      letterSpacing:
                        "0.06em",
                    }}
                  >
                    {isFR
                      ? "Après l’intégration"
                      : "After the import"}
                  </div>

                  <div
                    style={{
                      marginTop: 8,
                      fontWeight: 900,
                      lineHeight: 1.4,
                    }}
                  >
                    {
                      expectedResult
                        .title
                    }
                  </div>

                  <p
                    style={{
                      margin:
                        "8px 0 0",
                      lineHeight: 1.6,
                    }}
                  >
                    {
                      expectedResult
                        .body
                    }
                  </p>
                </section>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12,
                  marginTop: 12,
                }}
              >
                <label>
                  <span
                    style={{
                      display: "block",
                      marginBottom: 5,
                      fontWeight: 700,
                    }}
                  >
                    {isFR
                      ? "Où cette section doit-elle aller dans votre livre ?"
                      : "Where should this section go in your book?"}
                  </span>

                  <select
                    aria-label={
                      isFR
                        ? `Choix de page pour ${title}`
                        : `Page choice for ${title}`
                    }
                    value={
                      clean(
                        mappingBySection?.[
                          row.sectionId
                        ]
                      )
                    }
                    onChange={(event) =>
                      changeDestination(
                        row.sectionId,
                        event.target.value
                      )
                    }
                    style={{
                      width: "100%",
                      minHeight: 44,
                    }}
                  >
                    <option value="">
                      {isFR
                        ? "Choisir une page ou une double page"
                        : "Choose a page or two-page spread"}
                    </option>

                    {units.map(
                      (unit, unitIndex) => {
                        const unitId =
                          clean(unit?.id);

                        return (
                          <option
                            key={
                              unitId ||
                              unitIndex
                            }
                            value={unitId}
                            disabled={
                              usedByOthers
                                .has(unitId)
                            }
                          >
                            {destinationLabel(
                              unit,
                              unitIndex,
                              isFR,
                              units
                            )}
                          </option>
                        );
                      }
                    )}
                  </select>
                </label>

                <label>
                  <span
                    style={{
                      display: "block",
                      marginBottom: 5,
                      fontWeight: 700,
                    }}
                  >
                    {isFR
                      ? "Que souhaitez-vous faire de cette section ?"
                      : "What should happen to this section?"}
                  </span>

                  <select
                    aria-label={
                      isFR
                        ? `Choix d’intégration pour ${title}`
                        : `Import choice for ${title}`
                    }
                    value={
                      selectedDecision
                    }
                    onChange={(event) =>
                      changeDecision(
                        row.sectionId,
                        event.target.value
                      )
                    }
                    style={{
                      width: "100%",
                      minHeight: 44,
                    }}
                  >
                    {!selectedDecision ? (
                      <option value="">
                        {isFR
                          ? "Choisir ce qui doit arriver"
                          : "Choose what should happen"}
                      </option>
                    ) : null}

                    {allowed.map(
                      (decision) => (
                        <option
                          key={decision}
                          value={decision}
                        >
                          {
                            actionLabels[
                              decision
                            ]
                          }
                        </option>
                      )
                    )}
                  </select>
                </label>
              </div>

              {!row.placed &&
              selectedDecision !==
                MANUSCRIPT_IMPORT_DECISIONS
                  .SKIP ? (
                <p
                  style={{
                    margin:
                      "9px 0 0",
                    color: "#9a3412",
                    fontSize: 13,
                  }}
                >
                  {isFR
                    ? "Choisissez une page ou une double page, ou laissez cette section en dehors du livre."
                    : "Choose a page or two-page spread, or leave this section out of the book."}
                </p>
              ) : null}

              {selectedDecision ===
              MANUSCRIPT_IMPORT_DECISIONS
                .REPLACE ? (
                <p
                  role="alert"
                  style={{
                    margin:
                      "10px 0 0",
                    padding: 10,
                    borderRadius: 9,
                    background: "#fff7f7",
                    color: "#991b1b",
                    fontWeight: 650,
                  }}
                >
                  {isFR
                    ? `Les ${Number(row.existingWordCount || 0).toLocaleString()} mots actuellement dans ${displayTargetLabel} seront retirés et remplacés par les ${Number(row.importedWordCount || 0).toLocaleString()} mots de cette section Word. Une confirmation finale sera obligatoire.`
                    : `The ${Number(row.existingWordCount || 0).toLocaleString()} current words in ${displayTargetLabel} will be removed and replaced by the ${Number(row.importedWordCount || 0).toLocaleString()} words from this Word section. Final confirmation will be required.`}
                </p>
              ) : null}
            </article>
          );
        })}
      </div>
    </section>
  );
}
